import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { AccessApprovalCard, AccessApprovalRow, AccessApprovals, AccessCardSees, AccessPersonRow, ApiFailure } from '../api/types'
import type { Api } from '../api'
import { Badge, Button, Card, EmptyState, Loading, Notice, SearchInput, SearchSelect, Segmented } from '../components/ui'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { FailureState } from '../components/FailureState'
import { Warnings } from './AccessPeopleTab'
import {
  ApprovalConsequences, HIERARCHY, SEVERITY, STATUS, Fact, FilterSlot, Initials, Kbd, Meta, Panel, Verdict, ago, approveLabel, day, dayTime, failureOf, messageOf, roleLabel,
} from './accessWords'

/**
 * ACCESS › APPROVALS, under the new permission model (05 section 7.5; D8, D20, D37).
 *
 * Person by person, never in bulk (03 r23): each card shows everything wrong with
 * the person, where exactly it comes from, and what they will see if approved, and
 * the admin decides. There is no Decline (D37): someone not ready is left waiting,
 * or skipped.
 *
 * A STRAY KEY CAN NEVER APPROVE SOMEONE WHOSE PROBLEMS WERE NOT SEEN. "A" approves
 * only once the end of the problems list has been on screen; before that it scrolls
 * there. The approval quotes the card's warnings key, so the record holds exactly
 * what the approver was shown, and a change since is refused (409) and shown.
 *
 * THE LIST COMES A PAGE AT A TIME (owner, 27 Oct 2026): 500 people a page, searched
 * by name or email, and a line saying what is shown of how many, so "Tried to sign
 * in 4" above one person explains itself.
 *
 * Laid out again 27 Oct 2026 with the People page: the filters named above their
 * controls, the waiting list as rows that say who is picked, and the card as blocks
 * on their own surfaces with Skip and Approve at the bottom right. Approve now asks
 * first, naming what will be true afterwards; the question belongs to the card it was
 * asked on, and goes if that card does. A third view, Approved, lists every approval
 * that stands and takes one back, with a reason.
 */
const PAGE = 500
export function AccessApprovalsTab({ api, onOpenPerson, onCount }: {
  api: Api
  onOpenPerson: (id: number) => void
  /** The people who tried to sign in and wait, as this tab last read or changed it: the tab's count. */
  onCount?: (tried: number | null) => void
}) {
  const [view, setView] = useState<'tried' | 'waiting' | 'approved'>('tried')
  const [company, setCompany] = useState('')
  const [role, setRole] = useState('')
  const [problem, setProblem] = useState('')
  const [roleSetUp, setRoleSetUp] = useState<'yes' | 'no' | 'any'>('yes')
  // What is typed, and what is asked: the search waits until typing pauses.
  const [search, setSearch] = useState('')
  const [searchQ, setSearchQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])
  // The page belongs to one set of filters: any other set starts again at page 1.
  const filterKey = JSON.stringify([view, company, role, problem, roleSetUp, searchQ])
  const [pageAt, setPageAt] = useState({ key: filterKey, page: 1 })
  const page = pageAt.key === filterKey ? pageAt.page : 1
  const setPage = (n: number) => setPageAt({ key: filterKey, page: n })
  const [list, setList] = useState<AccessApprovals | null>(null)
  // The count is the list's own, so an approval moves it with no second read.
  const tried = list?.tried
  useEffect(() => {
    if (tried !== undefined) onCount?.(tried)
  }, [tried, onCount])
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [reload, setReload] = useState(0)
  const [picked, setPicked] = useState<number | null>(null)
  const [skipped, setSkipped] = useState<number[]>([])
  const [cards, setCards] = useState<Map<number, AccessApprovalCard>>(new Map())
  const [cardFailure, setCardFailure] = useState<{ id: number; failure: ApiFailure } | null>(null)
  // Whose problems have been seen, to the end: the person AND the warnings key shown.
  // Derived per card, never a flag carried from one card to the next (review, 26 Oct 2026).
  const [seenFor, setSeenFor] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'good' | 'bad' | 'warn'; text: string } | null>(null)
  const [undo, setUndo] = useState<{ id: number; name: string } | null>(null)
  const [approvedToday, setApprovedToday] = useState(0)
  const [companies, setCompanies] = useState<string[]>([])
  const [roles, setRoles] = useState<{ id: number; label: string }[]>([])
  const scrollRef = useRef<HTMLDivElement>(null)
  const endRef = useRef<HTMLDivElement>(null)

  // Approved has its own list (ApprovedView): there, this reads one row, only for the
  // two counts, so they stay current after a take-back (review, 27 Oct 2026).
  const countsOnly = view === 'approved'
  useEffect(() => {
    let cancelled = false
    setFailure(null)
    api
      .accessApprovals({
        view: countsOnly ? 'tried' : view,
        company: company || undefined,
        role: role ? Number(role) : undefined,
        problem: problem || undefined,
        roleSetUp,
        search: searchQ || undefined,
        page: countsOnly ? 1 : page,
        top: countsOnly ? 1 : PAGE,
      })
      .then((d) => {
        if (cancelled) return
        setList(d)
        setCompanies((seen) => [...new Set([...seen, ...d.people.map((p) => p.compId).filter((c): c is string => !!c)])].sort())
        setRoles((seen) => {
          const byId = new Map(seen.map((r) => [r.id, r]))
          for (const p of d.people) if (p.roleId !== null && p.roleName) byId.set(p.roleId, { id: p.roleId, label: p.roleName })
          return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label))
        })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setList(null)
        setFailure(failureOf(e))
      })
    return () => {
      cancelled = true
    }
  }, [api, view, countsOnly, company, role, problem, roleSetUp, searchQ, page, reload])

  // The filter menus offer every company and HR role, not only those of the people
  // on this page. A failed read leaves the menus built from the people read, as
  // before: nothing else depends on them, and the list reports its own failure.
  useEffect(() => {
    let cancelled = false
    api.accessOrgCodes()
      .then((o) => {
        if (!cancelled) setCompanies((seen) => [...new Set([...seen, ...o.codes.filter((c) => c.kind === 'COMPANY').map((c) => c.compId)])].sort())
      })
      .catch(() => undefined)
    api.accessHrRoles()
      .then((h) => {
        if (cancelled) return
        setRoles((seen) => {
          const byId = new Map(seen.map((r) => [r.id, r]))
          for (const r of h.roles) if (r.roleName) byId.set(r.roleId, { id: r.roleId, label: roleLabel(r) })
          return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label))
        })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [api])

  // Skipped people go to the end of the list, still there to come back to.
  const people = useMemo(() => {
    const all = list?.people ?? []
    return [...all.filter((p) => !skipped.includes(p.mindzerUserId)), ...all.filter((p) => skipped.includes(p.mindzerUserId))]
  }, [list, skipped])
  const current = picked !== null && people.some((p) => p.mindzerUserId === picked) ? picked : (people[0]?.mindzerUserId ?? null)
  const card = current !== null ? (cards.get(current) ?? null) : null
  const index = people.findIndex((p) => p.mindzerUserId === current)
  const cardKey = card?.person ? `${current}:${card.person.warningsKey}` : null
  const seenAll = cardKey !== null && seenFor === cardKey

  // A reload or another filter reads every card again: none stays from before.
  const inFlight = useRef(new Set<number>())
  useEffect(() => {
    setCards(new Map())
    inFlight.current.clear()
  }, [view, company, role, problem, roleSetUp, searchQ, page, reload])

  // The card on screen, and the next three, so the next appears at once. A request
  // is sent once and never cancelled by another card arriving.
  const mounted = useRef(true)
  // Set on every mount: React mounts twice in development, and a flag only ever
  // cleared would drop every card that arrives.
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  useEffect(() => {
    // None under Approved: its list is not this one (review, 27 Oct 2026).
    if (current === null || countsOnly) return
    const want = [current, ...people.slice(index + 1, index + 4).map((p) => p.mindzerUserId)]
      .filter((id) => !cards.has(id) && !inFlight.current.has(id))
    for (const id of want) {
      inFlight.current.add(id)
      api
        .accessApprovalCard(id)
        .then((c) => {
          if (!mounted.current) return
          setCards((m) => new Map(m).set(id, c))
          setCardFailure((f) => (f?.id === id ? null : f))
        })
        .catch((e: unknown) => mounted.current && setCardFailure({ id, failure: failureOf(e) }))
        .finally(() => inFlight.current.delete(id))
    }
  }, [api, current, index, people, cards, countsOnly])

  // How many are approved and in force, for the Approved option (owner, 27 Oct 2026:
  // "add one more here: approved"), read again whenever an approval here is made,
  // undone or taken back. readApproved says which (ApprovedView).
  const [approvedTotal, setApprovedTotal] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    readApproved(api, {})
      .then((people) => { if (!cancelled) setApprovedTotal(people.length) })
      .catch(() => { if (!cancelled) setApprovedTotal(null) })
    return () => {
      cancelled = true
    }
  }, [api, reload, approvedToday])

  // Has the end of the problems list been on screen, for THIS card? Measured
  // against the viewport, which also counts the card's own scroll area.
  useEffect(() => {
    const end = endRef.current
    if (!end || cardKey === null) return
    const io = new IntersectionObserver(([e]) => e?.isIntersecting && setSeenFor(cardKey), { threshold: 1 })
    io.observe(end)
    return () => io.disconnect()
  }, [cardKey])

  // The undo offer lasts ten seconds (D8).
  useEffect(() => {
    if (!undo) return
    const h = setTimeout(() => setUndo(null), 10_000)
    return () => clearTimeout(h)
  }, [undo])

  const move = (by: number) => {
    const next = people[Math.min(people.length - 1, Math.max(0, index + by))]
    if (next) setPicked(next.mindzerUserId)
  }

  const skip = () => {
    if (current === null) return
    const after = people[index + 1]
    setSkipped((s) => [...s.filter((x) => x !== current), current])
    if (after) setPicked(after.mindzerUserId)
  }

  const toTheEnd = () => {
    const area = scrollRef.current
    if (area) area.scrollTo({ top: area.scrollHeight })
  }

  // Approve asks first (owner, 27 Oct 2026): the button and "A" open the confirmation,
  // and only its own Approve approves. While it is open the keys do nothing.
  // The confirmation belongs to the card it opened for (person and warnings key): if
  // the card on screen changes under it, it is no longer shown, and its Approve does
  // nothing (review, 27 Oct 2026).
  const [confirming, setConfirming] = useState<string | null>(null)
  const confirmingThis = confirming !== null && confirming === cardKey && seenAll
  useEffect(() => {
    if (confirming !== null && confirming !== cardKey) setConfirming(null)
  }, [confirming, cardKey])
  const approve = () => {
    const person = card?.person
    if (!person || busy || current === null || confirming !== null) return
    if (!seenAll) {
      // Scroll to the end of the problems; the footer says why. No notice: one
      // above the card would shrink it and hide the end again.
      toTheEnd()
      return
    }
    setConfirming(cardKey)
  }

  const approveConfirmed = () => {
    const person = card?.person
    if (!person || busy || current === null || !confirmingThis) return
    const name = person.displayName ?? `Mindzer user ${person.mindzerUserId}`
    const after = people[index + 1] ?? people[index - 1] ?? null
    setBusy(true)
    setNotice(null)
    api
      .accessApprove(current, person.warningsKey)
      .then(() => {
        setApprovedToday((n) => n + 1)
        setUndo({ id: current, name })
        setCards((m) => {
          const n = new Map(m)
          n.delete(current)
          return n
        })
        setList((l) => {
          if (!l) return l
          const tried = l.people.find((p) => p.mindzerUserId === current)?.lastSignInUtc != null
          return {
            ...l,
            people: l.people.filter((p) => p.mindzerUserId !== current),
            waiting: l.waiting === null ? null : l.waiting - 1,
            tried: l.tried === null || !tried ? l.tried : l.tried - 1,
          }
        })
        setPicked(after?.mindzerUserId ?? null)
      })
      .catch((e: unknown) => {
        const f = failureOf(e)
        setNotice({ tone: 'bad', text: messageOf(e) })
        if (f.kind === 'error' && f.reason === 'WARNINGS_CHANGED') {
          // Show the warnings as they are now; the approver looks again.
          setCards((m) => {
            const n = new Map(m)
            n.delete(current)
            return n
          })
        }
      })
      .finally(() => {
        setBusy(false)
        setConfirming(null)
      })
  }

  const undoApproval = () => {
    if (!undo) return
    const { id, name } = undo
    setUndo(null)
    api
      .accessWithdraw(id, 'Undone within 10 seconds')
      .then(() => {
        setNotice({ tone: 'good', text: `${name}'s approval was undone. They are waiting again.` })
        setApprovedToday((n) => Math.max(0, n - 1))
        setReload((n) => n + 1)
      })
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
  }

  // Keyboard: A approve, S skip, up and down move. Never while typing.
  const keys = useRef({ approve, skip, move, confirming, view })
  // Before the browser paints, so a key pressed on a new card never runs the last card's handler.
  useLayoutEffect(() => {
    keys.current = { approve, skip, move, confirming, view }
  })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      // A held key repeats: it must never approve a run of people.
      if (e.repeat) return
      // The confirmation has the keyboard: Escape and its own buttons only. Approved
      // has nothing to approve or skip.
      if (keys.current.confirming !== null || keys.current.view === 'approved') return
      if (e.ctrlKey || e.metaKey || e.altKey || (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) || t?.isContentEditable) return
      if (e.key === 'a' || e.key === 'A') { e.preventDefault(); keys.current.approve() }
      else if (e.key === 's' || e.key === 'S') { e.preventDefault(); keys.current.skip() }
      else if (e.key === 'ArrowDown') { e.preventDefault(); keys.current.move(1) }
      else if (e.key === 'ArrowUp') { e.preventDefault(); keys.current.move(-1) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const problemOptions = [
    { value: '', label: 'Any' },
    { value: 'NONE', label: 'None' },
    ...Object.entries(SEVERITY).map(([value, s]) => ({ value, label: s.label })),
  ]
  const narrowed = !!(company || role || problem || roleSetUp !== 'any' || searchQ)

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {confirmingThis && card?.person && (
        <ConfirmDialog
          title={`Approve ${card.person.displayName ?? `Mindzer user ${card.person.mindzerUserId}`}?`}
          detail={<ApprovalConsequences card={card} />}
          confirmLabel={approveLabel(card.warnings)}
          tone={card.person.widens ? 'danger' : 'default'}
          busy={busy}
          onConfirm={approveConfirmed}
          onCancel={() => setConfirming(null)}
        />
      )}
      {(notice || undo) && (
        <div className="shrink-0 space-y-2">
          {undo && (
            <Notice tone="good" onDismiss={() => setUndo(null)}>
              Approved {undo.name}.{' '}
              <button type="button" onClick={undoApproval} className="font-semibold underline">
                Undo
              </button>
            </Notice>
          )}
          {notice && (
            <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>
              {notice.text}
            </Notice>
          )}
        </div>
      )}

      <Card className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-wrap items-end gap-x-3 gap-y-3 border-b border-(--color-line) px-5 py-(--band-y)">
          <div className="flex flex-col gap-1">
            <span className="eyebrow">Who</span>
            <Segmented
              label="Who"
              value={view}
              onChange={(v) => { setView(v); setPicked(null) }}
              options={[
                { value: 'tried', label: `Tried to sign in${list?.tried != null ? ` · ${list.tried.toLocaleString('en-GB')}` : ''}` },
                { value: 'waiting', label: `All waiting${list?.waiting != null ? ` · ${list.waiting.toLocaleString('en-GB')}` : ''}` },
                { value: 'approved', label: `Approved${approvedTotal != null ? ` · ${approvedTotal.toLocaleString('en-GB')}` : ''}` },
              ]}
            />
          </div>
          <div className="flex min-w-[190px] flex-[1.2] flex-col gap-1 @6xl:max-w-[280px]">
            <span className="eyebrow">Find a user</span>
            <SearchInput
              label={view === 'approved' ? 'Find a user by name, email, company or HR Role' : 'Find a user by name or email'}
              placeholder={view === 'approved' ? 'Name, email, company or HR Role…' : 'Name or email'}
              value={search} onChange={setSearch} />
          </div>
          <FilterSlot label="Company">
            <SearchSelect label="Company" value={company} onChange={setCompany}
              options={[{ value: '', label: 'All companies' }, ...companies.map((c) => ({ value: c, label: c }))]} />
          </FilterSlot>
          <FilterSlot label="HR Role" wide>
            <SearchSelect label="HR Role" keepCase value={role} onChange={setRole}
              options={[{ value: '', label: 'All HR Roles' }, ...roles.map((r) => ({ value: String(r.id), label: r.label }))]} />
          </FilterSlot>
          {/* About who waits: an approved person's problems and role are on their card. */}
          {view !== 'approved' && (
            <>
              <FilterSlot label="Problems">
                <SearchSelect label="Problems" value={problem} onChange={setProblem} options={problemOptions} />
              </FilterSlot>
              {/* Owner's words, 30 Oct 2026; the space before "?" never breaks. */}
              <FilterSlot label={'HR Role Configured\u00a0?'}>
                <SearchSelect label="HR Role Configured" keepCase value={roleSetUp} onChange={(v) => setRoleSetUp(v as 'yes' | 'no' | 'any')}
                  options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'any', label: 'Any' }]} />
              </FilterSlot>
            </>
          )}
        </div>

        {view === 'approved' ? (
          <ApprovedView api={api} search={searchQ} company={company} role={role} onOpenPerson={onOpenPerson}
            onChanged={() => setReload((n) => n + 1)} />
        ) : (<>
        {list && people.length > 0 && (
          <Showing list={list} view={view} page={page} approvedToday={approvedToday} hiddenBy={[
            company && `company ${company}`,
            role && `HR Role ${roles.find((r) => String(r.id) === role)?.label ?? role}`,
            problem && `problems: ${problemOptions.find((o) => o.value === problem)?.label ?? problem}`,
            roleSetUp !== 'any' && `HR Role Configured: ${roleSetUp === 'yes' ? 'Yes' : 'No'}`,
            searchQ && `search “${searchQ}”`,
          ].filter((x): x is string => !!x)}
            onPage={(n) => { setPage(n); setPicked(null) }}
            onShowEveryone={() => {
              setCompany(''); setRole(''); setProblem(''); setRoleSetUp('any'); setSearch(''); setSearchQ(''); setPicked(null)
            }} />
        )}

        {/* With nobody left to show, what you approved here stays said. */}
        {list && people.length === 0 && approvedToday > 0 && (
          <div className="flex shrink-0 justify-end border-b border-(--color-line) bg-(--color-surface-2) px-5 py-1.5">
            <Badge tone="good">You approved {approvedToday} here</Badge>
          </div>
        )}

        {failure ? (
          <FailureState failure={failure} onRetry={() => setReload((n) => n + 1)} />
        ) : list === null ? (
          <Loading label="Reading who is waiting…" />
        ) : people.length === 0 ? (
          <EmptyState
            title={narrowed
              ? 'Nobody here matches these filters.'
              : view === 'tried' ? 'Nobody at the door.' : 'Nobody is waiting.'}
            detail={narrowed
              ? '“HR Role Configured\u00a0?” is Yes by default: set it to Any, or clear a filter or the search, to see everyone.'
              : view === 'tried'
                ? 'No one waiting has tried to sign in. See All waiting for everyone undecided.'
                : 'Everyone Mindzer sends is approved, or needs no approval.'}
          />
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 bg-(--color-surface-2) @5xl:grid-cols-[340px_1fr]">
            <ul className="flex min-h-0 flex-col gap-1.5 overflow-y-auto border-(--color-line) p-3 @5xl:border-r" aria-label="Users waiting">
              {people.map((p) => (
                <WaitingRow key={p.mindzerUserId} p={p} on={p.mindzerUserId === current}
                  skipped={skipped.includes(p.mindzerUserId)} onPick={() => setPicked(p.mindzerUserId)} />
              ))}
            </ul>

            <div className="flex min-h-0 flex-col bg-(--color-surface)">
              {cardFailure && cardFailure.id === current && !card ? (
                <FailureState failure={cardFailure.failure} onRetry={() => { setCardFailure(null); setCards((m) => new Map(m)) }} />
              ) : !card?.person ? (
                <Loading label="Reading their approval card…" />
              ) : (
                <>
                  <CardHead card={card} onOpen={() => onOpenPerson(card.person!.mindzerUserId)} />
                  {/* A new scroll area per person: a card never opens where the last one was scrolled to. */}
                  <div key={current ?? 'none'} ref={scrollRef} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-(--color-surface-2) px-5 py-4">
                    <Sees card={card} />
                    <Panel title={card.warnings.length ? `Problems · ${card.warnings.length}` : 'Problems'}>
                      <Warnings warnings={card.warnings} />
                    </Panel>
                    <div ref={endRef} className="h-px shrink-0" />
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-2 border-t border-(--color-line) bg-(--color-surface) px-5 py-3">
                    <span className="mr-auto flex flex-wrap items-center gap-x-1.5 gap-y-1 text-(length:--text-small) text-(--color-ink-3)">
                      {/* Reading to the end comes first for everyone: until then Approve only scrolls there. */}
                      {!seenAll ? (
                        <>
                          Read to the end of the problems to approve.
                          <button type="button" onClick={toTheEnd} className="font-medium text-(--color-navy-700) hover:underline">Take me there ↓</button>
                        </>
                      ) : !card.person.roleSetUp
                        ? 'Approving does not let them in until their HR Role is configured.'
                        : card.person.blocksEntry
                          ? 'They still cannot enter until the problem marked Cannot enter is fixed.'
                          : (
                            <>
                              <Kbd>A</Kbd> approves <span aria-hidden>·</span> <Kbd>S</Kbd> skips <span aria-hidden>·</span> <Kbd>↑</Kbd><Kbd>↓</Kbd> move
                            </>
                          )}
                    </span>
                    <Button variant="ghost" onClick={skip}>Skip</Button>
                    <Button variant={card.person.widens ? 'danger' : 'primary'} onClick={approve} disabled={busy}>
                      {approveLabel(card.warnings)}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        </>)}
      </Card>
    </div>
  )
}

/**
 * Everyone whose approval counts, as the server says (17f: People's
 * Approval_In_Force, sec.v_Approval_In_Force_MBI), newest first.
 *
 * NOT FROM THE BADGES. A person's one badge is worked out in order
 * (sec.fn_Person_Status_MBI), and system role, disabled, no company email, no HR role and
 * a role not set up all come BEFORE "back after leaving" and "gained more", so a badge
 * cannot say whether an approval still counts. Read from the badges, this view either
 * missed approved people kept out by something else or offered to take back
 * approvals that no longer counted (second review, 27 Oct 2026).
 */
async function readApproved(api: Api, filter: { search?: string; company?: string; role?: number }): Promise<AccessPersonRow[]> {
  // Every page (17g): the list comes 2,000 at a time by name, and "newest first" over the
  // first page alone would leave the newest out once more than 2,000 are approved.
  const all = new Map<number, AccessPersonRow>()
  for (let page = 1; page <= 50; page++) {
    const d = await api.accessPeople({ ...filter, approved: true, page })
    for (const p of d.people) all.set(p.mindzerUserId, p)
    if (d.people.length < 2000 || d.listed == null || all.size >= d.listed) break
  }
  return [...all.values()].sort((x, y) => (y.decidedUtc ?? '').localeCompare(x.decidedUtc ?? ''))
}

/**
 * APPROVED (owner, 27 Oct 2026: "add one more here: approved"): everyone whose
 * approval counts (readApproved), newest first, read from the People list. A row
 * kept out by something else (disabled, no HR role) says so. Each card says what they
 * see and what is wrong, who approved them and when, and offers to take the approval
 * back, with a reason, behind a confirmation.
 */
function ApprovedView({ api, search, company, role, onOpenPerson, onChanged }: {
  api: Api
  search: string
  company: string
  role: string
  onOpenPerson: (id: number) => void
  /** An approval was taken back: the counts and the waiting lists are read again. */
  onChanged: () => void
}) {
  const [rows, setRows] = useState<AccessPersonRow[] | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [reload, setReload] = useState(0)
  const [picked, setPicked] = useState<number | null>(null)
  const [card, setCard] = useState<AccessApprovalCard | null>(null)
  const [cardFailure, setCardFailure] = useState<ApiFailure | null>(null)
  const [cardReload, setCardReload] = useState(0)
  // Whose approval the take-back is for: the dialog and its reason belong to that one person.
  const [withdrawing, setWithdrawing] = useState<number | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setFailure(null)
    const filter = { search: search || undefined, company: company || undefined, role: role ? Number(role) : undefined }
    readApproved(api, filter)
      .then((people) => {
        if (!cancelled) setRows(people)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setRows(null)
        setFailure(failureOf(e))
      })
    return () => {
      cancelled = true
    }
  }, [api, search, company, role, reload])

  const current = picked !== null && rows?.some((r) => r.mindzerUserId === picked) ? picked : (rows?.[0]?.mindzerUserId ?? null)

  useEffect(() => {
    if (current === null) return
    let cancelled = false
    setCard(null)
    setCardFailure(null)
    api.accessApprovalCard(current)
      .then((c) => {
        if (!cancelled) setCard(c)
      })
      .catch((e: unknown) => !cancelled && setCardFailure(failureOf(e)))
    return () => {
      cancelled = true
    }
  }, [api, current, cardReload])

  const name = card?.person ? card.person.displayName ?? `Mindzer user ${card.person.mindzerUserId}` : ''
  // What the approval does for them now (review, 27 Oct 2026): it lets them in; a
  // system role lets them in whatever it says; or something else keeps them out.
  const row = rows?.find((r) => r.mindzerUserId === current) ?? null
  const effect = !row || row.status === 'CAN_ENTER' || row.status === 'NO_SIGN_IN' ? 'entry'
    : row.status === 'SEES_ALL' ? 'system' : 'kept'
  // Mid-sentence, so a first word drops its capital ("No HR role" -> "no HR role"); "HR" keeps its own.
  const keptBy = row && effect === 'kept' ? (STATUS[row.status]?.label ?? row.status).replace(/^[A-Z](?=[a-z])/, (c) => c.toLowerCase()) : ''
  const withdraw = () => {
    const id = withdrawing
    if (id === null || id !== current || busy || !reason.trim()) return
    const after = effect === 'entry' ? 'They are waiting again.'
      : effect === 'system' ? 'Their system role still lets them in.'
        : 'Once nothing else keeps them out, they wait to be approved again.'
    setBusy(true)
    setNotice(null)
    api.accessWithdraw(id, reason.trim())
      .then(() => {
        setNotice({ tone: 'good', text: `${name}'s approval was taken back. ${after}` })
        // Off the list at once, before it is read again.
        setRows((r) => r?.filter((x) => x.mindzerUserId !== id) ?? r)
        setPicked(null)
        setReload((n) => n + 1)
        onChanged()
      })
      // Said in front, not under the dialog's scrim.
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
      .finally(() => {
        setBusy(false)
        setWithdrawing(null)
        setReason('')
      })
  }

  const narrowed = !!(search || company || role)
  return (
    <>
      {notice && (
        <div className="shrink-0 border-b border-(--color-line) px-5 py-2">
          <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Notice>
        </div>
      )}
      {rows && rows.length > 0 && (
        <div className="flex shrink-0 items-center gap-3 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-1.5 text-(length:--text-small) text-(--color-ink-3)">
          Showing {rows.length.toLocaleString('en-GB')} approved, newest first{narrowed ? ', matching your filters' : ''}.
          <span className="ml-auto">"Kept out" means approved, but something else stops them (disabled, no HR Role).</span>
        </div>
      )}
      {failure ? (
        <FailureState failure={failure} onRetry={() => setReload((n) => n + 1)} />
      ) : rows === null ? (
        <Loading label="Reading who is approved…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title={narrowed ? 'Nobody approved matches these filters.' : 'Nobody is approved yet.'}
          detail={narrowed ? 'Clear a filter or the search to see everyone approved.' : 'Users you approve appear here, newest first.'}
        />
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 bg-(--color-surface-2) @5xl:grid-cols-[340px_1fr]">
          <ul className="flex min-h-0 flex-col gap-1.5 overflow-y-auto border-(--color-line) p-3 @5xl:border-r" aria-label="Users approved">
            {rows.map((r) => (
              <ApprovedRow key={r.mindzerUserId} p={r} on={r.mindzerUserId === current} onPick={() => setPicked(r.mindzerUserId)} />
            ))}
          </ul>
          <div className="flex min-h-0 flex-col bg-(--color-surface)">
            {cardFailure ? (
              <FailureState failure={cardFailure} onRetry={() => setCardReload((n) => n + 1)} />
            ) : !card?.person ? (
              <Loading label="Reading their card…" />
            ) : (
              <>
                <CardHead card={card} inForce onOpen={() => onOpenPerson(card.person!.mindzerUserId)} />
                <div key={current ?? 'none'} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-(--color-surface-2) px-5 py-4">
                  <Sees card={card}
                    title={effect === 'entry' ? 'They see' : effect === 'system' ? 'Their approval gives' : 'Once let in, they would see'} />
                  <Panel title={card.warnings.length ? `Problems · ${card.warnings.length}` : 'Problems'}>
                    <Warnings warnings={card.warnings} />
                  </Panel>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-2 border-t border-(--color-line) bg-(--color-surface) px-5 py-3">
                  <span className="mr-auto text-(length:--text-small) text-(--color-ink-3)">
                    Approved {dayTime(card.person.decidedUtc)}{card.person.decidedByEmail ? ` by ${card.person.decidedByEmail}` : ''}
                    {card.person.approvedEmail ? `, for ${card.person.approvedEmail}` : ''}.
                  </span>
                  <Button variant="danger" onClick={() => { setReason(''); setWithdrawing(card.person!.mindzerUserId) }} disabled={busy}>Take approval back…</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {withdrawing !== null && withdrawing === current && card?.person?.mindzerUserId === withdrawing && (
        <ConfirmDialog
          title={`Take back ${name}'s approval?`}
          detail={
            <div className="flex flex-col gap-2 text-(length:--text-small) text-(--color-ink-2)">
              <p>
                {effect === 'entry' ? 'They lose entry to MBI at their next request, and wait to be approved again.'
                  : effect === 'system' ? 'Their system role lets them in and see everything whatever the approval, so nothing changes now. If the role is removed, they need approving again.'
                    : `They are already kept out (${keptBy}). Once that is fixed, they need approving again.`}
                {' '}The reason is kept with the record and shown on their page.
              </p>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason Of The Change (required)" aria-label="Reason Of The Change: taking the approval back (required)"
                className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-2 text-(length:--text-small) text-(--color-ink)" />
            </div>
          }
          confirmLabel="Take approval back"
          tone="danger"
          busy={busy}
          confirmDisabled={!reason.trim()}
          focusInput
          onConfirm={withdraw}
          onCancel={() => { setWithdrawing(null); setReason('') }}
        />
      )}
    </>
  )
}

/**
 * One person approved: who, their company and role, whether they have signed in (or
 * what keeps them out, when something else does), and when approved.
 */
function ApprovedRow({ p, on, onPick }: { p: AccessPersonRow; on: boolean; onPick: () => void }) {
  const name = p.displayName ?? `Mindzer user ${p.mindzerUserId}`
  const place = [p.compId, p.roleName].filter(Boolean).join(' · ') || 'No company or role'
  // A system role sees everything whatever the approval: that is not being kept out.
  // Can enter means approved and linked; an account found for Login As (D51) is
  // linked before its first sign-in, so "Signed in" asks whether they ever did.
  const state = p.status === 'CAN_ENTER' && p.lastSignInUtc ? { text: 'Signed in', look: 'bg-(--color-good)/10 text-(--color-good)' }
    : p.status === 'CAN_ENTER' || p.status === 'NO_SIGN_IN' ? { text: 'Not signed in yet', look: 'bg-(--color-surface-3) text-(--color-ink-3)' }
      : p.status === 'SEES_ALL' ? { text: STATUS.SEES_ALL.label, look: 'bg-(--color-privileged)/10 text-(--color-privileged)' }
        : { text: `Kept out: ${STATUS[p.status]?.label ?? p.status}`, look: 'bg-(--color-warn)/10 text-(--color-warn)' }
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        aria-current={on ? 'true' : undefined}
        className={`flex w-full items-center gap-2.5 rounded-md border px-3 py-2 text-left text-(length:--text-small) transition-colors ${
          on ? 'border-(--color-navy-700) bg-(--color-navy-50) ring-1 ring-(--color-navy-700)'
            : 'border-(--color-line) bg-(--color-surface) hover:border-(--color-navy-300) hover:bg-(--color-navy-50)'
        }`}
      >
        <Initials name={name} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-(--color-ink)" title={name}>{name}</span>
          <span className="block truncate text-(--color-ink-3)" title={place}>{place}</span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          <span className={`inline-flex items-center rounded-full px-1.5 text-(length:--text-micro) font-semibold ${state.look}`}>
            {state.text}
          </span>
          <span className="text-(length:--text-micro) text-(--color-ink-4)">approved {day(p.decidedUtc)}</span>
        </span>
      </button>
    </li>
  )
}

/** One person waiting: who, their company and role, their problems and when they last tried. */
function WaitingRow({ p, on, skipped, onPick }: { p: AccessApprovalRow; on: boolean; skipped: boolean; onPick: () => void }) {
  const name = p.displayName ?? `Mindzer user ${p.mindzerUserId}`
  const notes = [p.decision === 'WITHDRAWN' ? 'approval taken back' : null, p.returned ? 'back after leaving' : null, skipped ? 'skipped' : null].filter(Boolean)
  const place = [p.compId, p.roleName].filter(Boolean).join(' · ') || 'No company or role'
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        aria-current={on ? 'true' : undefined}
        className={`flex w-full items-center gap-2.5 rounded-md border px-3 py-2 text-left text-(length:--text-small) transition-colors ${
          on ? 'border-(--color-navy-700) bg-(--color-navy-50) ring-1 ring-(--color-navy-700)'
            : 'border-(--color-line) bg-(--color-surface) hover:border-(--color-navy-300) hover:bg-(--color-navy-50)'
        } ${skipped ? 'opacity-70' : ''}`}
      >
        <Initials name={name} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-(--color-ink)" title={name}>{name}</span>
          <span className="block truncate text-(--color-ink-3)" title={place}>{place}</span>
          {notes.length > 0 && <span className="block truncate text-(--color-warn)">{notes.join(' · ')}</span>}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          {p.warningCount > 0 && (
            <span
              title={`${p.warningCount} ${p.warningCount === 1 ? 'problem' : 'problems'}${p.widens ? ': it would widen their rows' : ''}`}
              className={`inline-flex items-center rounded-full px-1.5 text-(length:--text-micro) font-semibold ${
                p.widens ? 'bg-(--color-overdue)/10 text-(--color-overdue)' : 'bg-(--color-warn)/10 text-(--color-warn)'}`}>
              ⚠ {p.warningCount}{p.widens && <span className="sr-only"> problems, one widens their rows</span>}
            </span>
          )}
          <span className="text-(length:--text-micro) text-(--color-ink-4)">{p.lastSignInUtc ? ago(p.lastSignInUtc) : 'never tried'}</span>
        </span>
      </button>
    </li>
  )
}

function CardHead({ card, onOpen, inForce = false }: {
  card: AccessApprovalCard
  onOpen: () => void
  /** Shown under Approved: the approval stands, so there is nothing to look at again. */
  inForce?: boolean
}) {
  const p = card.person!
  const name = p.displayName ?? `Mindzer user ${p.mindzerUserId}`
  const managers = [p.lineManagerId && `line: user ${p.lineManagerId}`, p.funcManagerId && `functional: user ${p.funcManagerId}`].filter(Boolean)
  // Waiting though approved: back after leaving, or an update gave them more since.
  const again = !inForce && p.approval === 'APPROVED' ? (p.returned ? 'Approved before they left: look again.' : 'Approved before an update gave them more: look again.') : null
  return (
    <div className="flex shrink-0 items-start gap-3.5 border-b border-(--color-line) px-5 py-3">
      <Initials name={name} size="lg" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <h3 className="text-(length:--text-xl) font-semibold tracking-[-0.015em] text-(--color-ink)">{name}</h3>
          {p.roleName && <Badge tone={p.roleSetUp ? 'good' : 'bad'}>{p.roleSetUp ? 'HR Role Configured' : 'HR Role not configured'}</Badge>}
          {card.warnings.length > 0 && <Badge tone={p.widens ? 'bad' : 'warn'}>⚠ {card.warnings.length} {card.warnings.length === 1 ? 'problem' : 'problems'}</Badge>}
        </div>
        <dl className="flex flex-wrap gap-x-5 gap-y-1 text-(length:--text-small)">
          <Meta label="Main company" value={p.compId ? [p.entityName, p.compId].filter(Boolean).join(' · ') : 'none from HR'} />
          <Meta label="HR Role" value={p.roleName ? roleLabel(p) : 'No HR Role'} />
          <Meta label="Sign-in" value={p.lastSignInUtc ? `tried ${dayTime(p.lastSignInUtc)}` : 'never signed in to MBI'} />
          <Meta label="Managers" value={managers.length ? managers.join(' · ') : 'none in Mindzer'} />
        </dl>
        {p.approval === 'WITHDRAWN' && (
          <p className="text-(length:--text-small) text-(--color-warn)">Approval taken back{p.decisionReason ? `: “${p.decisionReason}”` : '.'}</p>
        )}
        {again && <p className="text-(length:--text-small) text-(--color-warn)">{again}</p>}
      </div>
      <button type="button" onClick={onOpen}
        className="shrink-0 rounded px-1 text-(length:--text-small) font-medium text-(--color-navy-700) hover:underline">
        Open their page →
      </button>
    </div>
  )
}

/** Where a card line comes from (049 section 4: M Mindzer, A a personal add, X an extra scope), in
 *  the person page's words (owner, 2 Nov 2026). */
const LINE_SOURCE: Record<string, string> = {
  M: 'From HR', PLACEMENT: 'From HR', A: 'Manual Override', X: 'MBI additional scope', EXTRA: 'MBI additional scope',
}

/**
 * If you approve, they will see: every report they could open, by scope line, where
 * the line comes from, its column groups (D53: a line's groups are its report's own),
 * and its rows in the last 12 months (counts, never rows); then hierarchy and phone.
 */
function Sees({ card, title = 'If you approve, they will see' }: {
  card: AccessApprovalCard
  title?: string
}) {
  const p = card.person!
  const lineWords = (s: AccessCardSees) =>
    s.compId === null ? 'no scope: the report opens empty'
      : [s.compId, s.anyUnit ? 'all Business Units' : `Business Unit ${s.businessUnit ?? '?'}`,
          s.anyTeam ? 'all Business Teams' : `Business Team ${s.businessTeam ?? '?'}`].join(' · ')
  const inh = card.inherits
  return (
    <Panel title={title} bodyClassName="!px-0 !py-0">
      {card.sees.length === 0 ? (
        <div className="px-4 py-3">
          <Verdict ok={false} title="No report access">Their HR Role and overrides give them none.</Verdict>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-(length:--text-small)">
            <thead>
              <tr className="bg-(--color-surface-2) text-left text-(--color-ink-3)">
                <th className="px-4 py-2 font-medium">Report</th>
                <th className="px-4 py-2 font-medium">Where</th>
                <th className="px-4 py-2 font-medium">Comes from</th>
                <th className="whitespace-nowrap px-4 py-2 font-medium">Column groups</th>
                <th className="whitespace-nowrap px-4 py-2 text-right font-medium">Rows, last 12 months</th>
              </tr>
            </thead>
            <tbody>
              {card.sees.map((s, i) => (
                <tr key={`${s.reportSlug}-${s.scopeKey}-${i}`} className="border-t border-(--color-line) transition-colors hover:bg-(--color-navy-50)">
                  <td className="whitespace-nowrap px-4 py-2 font-medium text-(--color-ink)">{s.displayName}</td>
                  <td className="px-4 py-2 text-(--color-ink-2)">{lineWords(s)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-(--color-ink-3)">
                    {s.lineSource ? LINE_SOURCE[s.lineSource] ?? s.lineSource : s.scopeKey !== 0 ? 'MBI additional scope' : 'HR main scope'}
                  </td>
                  <td className="px-4 py-2 text-(--color-ink-2)">
                    {s.columnGroups ?? <span className="text-(--color-ink-4)">None</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right font-medium text-(--color-ink) tabular-nums">
                    {s.rowsRecent === null ? <span className="font-normal text-(--color-ink-4)">—</span> : s.rowsRecent.toLocaleString('en-GB')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="grid grid-cols-1 gap-2 border-t border-(--color-line) px-4 py-3 @3xl:grid-cols-2">
        <Fact label="Hierarchy" value={HIERARCHY[p.hierarchyMode] ?? 'Off'} />
        <Fact label="Phone" value={p.mobileAllowed ? 'Allowed' : 'Not allowed'} />
      </div>
      {inh && inh.hierarchyMode === 2 && inh.staffWithScopes > 0 && (
        <div className="px-4 pb-3">
          <Verdict ok={null} tone="warn" title={`They would also inherit ${inh.inheritedLines} ${inh.inheritedLines === 1 ? 'line' : 'lines'} from ${inh.staffWithScopes} staff`}>
            As a type-2 manager they see each staff member’s lines with that member’s column groups
            {inh.staffWithProblems > 0 ? `; ${inh.staffWithProblems} of those staff have problems.` : '.'}
          </Verdict>
        </div>
      )}
    </Panel>
  )
}

/**
 * What the list shows of how many (owner, 27 Oct 2026): "Showing 1 of the 4. 3 are
 * hidden by your filters (HR Role Configured: Yes)." with a way to show everyone, and the
 * pages when there is more than one; "Showing all 3." when nothing is hidden. Always
 * over the list, with what you approved here at its right.
 */
function Showing({ list, view, page, approvedToday, hiddenBy, onPage, onShowEveryone }: {
  list: AccessApprovals
  view: 'tried' | 'waiting'
  page: number
  approvedToday: number
  hiddenBy: string[]
  onPage: (page: number) => void
  onShowEveryone: () => void
}) {
  const total = view === 'tried' ? list.tried : list.waiting
  const listed = list.listed ?? list.people.length
  const pages = Math.max(1, Math.ceil(listed / PAGE))
  const from = (page - 1) * PAGE + 1
  const to = Math.min(listed, page * PAGE)
  const range = pages > 1 ? `${from.toLocaleString()}–${to.toLocaleString()}` : listed.toLocaleString()
  const hidden = total !== null && listed < total ? total - listed : 0
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-1.5 text-(length:--text-small) text-(--color-ink-3)">
      <span>
        {hidden || pages > 1 ? `Showing ${range} of ${hidden ? `the ${(total ?? listed).toLocaleString()}` : listed.toLocaleString()}.` : `Showing all ${listed.toLocaleString()}.`}
        {hidden > 0 && ` ${hidden.toLocaleString()} ${hidden === 1 ? 'is' : 'are'} hidden by your filters${hiddenBy.length ? ` (${hiddenBy.join(', ')})` : ''}.`}
      </span>
      {hidden > 0 && <Button variant="ghost" size="sm" onClick={onShowEveryone}>Show everyone</Button>}
      <span className="ml-auto flex items-center gap-3">
        {approvedToday > 0 && <Badge tone="good">You approved {approvedToday} here</Badge>}
        {pages > 1 && (
          <span className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹ Previous</Button>
            <span>Page {page} of {pages}</span>
            <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next ›</Button>
          </span>
        )}
      </span>
    </div>
  )
}
